import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Pill } from "@/components/Music";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { money } from "@/components/Royalty";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUS = {
  ready: ["good", "Ready to pay"],
  below: ["warn", "Below minimum"],
  hold: ["bad", "On hold"],
  paid: ["muted", "Paid up"],
  none: ["muted", "Nothing owed"],
};

export function StatusPill({ status }) {
  const [tone, label] = STATUS[status] || STATUS.none;
  return <Pill tone={tone}>{label}</Pill>;
}

function NumberBox({ value, onChange, unit, before, label, disabled }) {
  return (
    <span className={cn("inline-flex items-center h-10 rounded-lg border border-line bg-card overflow-hidden", disabled && "opacity-60")}>
      {before && <span className="px-2.5 h-full flex items-center bg-surface text-muted2 text-[13px]">{unit}</span>}
      <input
        type="number"
        min="0"
        step={before ? "1" : "0.5"}
        inputMode="decimal"
        aria-label={label}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-[76px] h-full bg-transparent text-right font-mono text-[14px] px-2 outline-none"
      />
      {!before && <span className="px-2.5 h-full flex items-center bg-surface text-muted2 text-[13px]">{unit}</span>}
    </span>
  );
}

function Row({ title, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5 border-t border-line first:border-t-0 flex-wrap">
      <div className="min-w-0 flex-1 basis-[220px]">
        <div className="text-[13.5px] font-semibold">{title}</div>
        {hint && <p className="text-[12.5px] text-muted2 mt-0.5 max-w-[56ch]">{hint}</p>}
      </div>
      <div className="flex items-center gap-2.5 shrink-0">{children}</div>
    </div>
  );
}

/** One writer's own percentage, minimum and hold — used on the Payouts drawer and the Writers page. */
export function WriterTerms({ writerId, terms, defaults, onSaved, canEdit }) {
  const [t, setT] = useState({ rate: null, min: null, hold: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => setT({ rate: terms?.rate ?? null, min: terms?.min ?? null, hold: !!terms?.hold }), [terms, writerId]);
  const save = async (next) => {
    setT(next);
    setBusy(true);
    try {
      await api.put(`/writers/${writerId}/payout-terms`, { rate: next.rate === "" ? null : next.rate == null ? null : Number(next.rate), min: next.min === "" ? null : next.min == null ? null : Number(next.min), hold: next.hold });
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <fieldset disabled={!canEdit}>
      <Row title="Use a different percentage" hint={`The default is ${defaults.rate}%.`}>
        <Switch checked={t.rate != null} onCheckedChange={(v) => save({ ...t, rate: v ? defaults.rate : null })} aria-label="Use a different percentage" />
        {t.rate != null && <NumberBox value={t.rate} unit="%" label="This writer's percentage" onChange={(v) => setT({ ...t, rate: v })} />}
      </Row>
      <Row title="Use a different minimum" hint={`The default is ${money(defaults.min, { places: 2 })}.`}>
        <Switch checked={t.min != null} onCheckedChange={(v) => save({ ...t, min: v ? defaults.min : null })} aria-label="Use a different minimum" />
        {t.min != null && <NumberBox value={t.min} unit="$" before label="This writer's minimum" onChange={(v) => setT({ ...t, min: v })} />}
      </Row>
      <Row title="Hold payouts" hint="Their balance keeps adding up but never shows as ready. For a dispute or missing bank details.">
        <Switch checked={t.hold} onCheckedChange={(v) => save({ ...t, hold: v })} aria-label="Hold payouts" />
      </Row>
      {canEdit && (t.rate != null || t.min != null) && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => save(t).then(() => toast.success("Saved"))} className="mt-1">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save their terms"}
        </Button>
      )}
    </fieldset>
  );
}

/** Settings › Writers: the defaults for everyone, and what writers may see. */
export function PayoutSettings() {
  const { setSettings } = useSettings();
  const [s, setS] = useState(null);
  const [draft, setDraft] = useState({ rate: "", min: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => {
        setS(data.payouts);
        setDraft({ rate: data.payouts.rate, min: data.payouts.min });
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);
  const put = async (patch, quiet) => {
    setS((v) => ({ ...v, ...patch }));
    setBusy(true);
    try {
      const { data } = await api.put("/payouts/settings", patch);
      setS(data);
      setSettings((v) => ({ ...v, payouts: data }));
      if (!quiet) toast.success("Saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (!s) return <div className="panel panel-pad min-h-[420px]" />;
  const sw = (key, label) => <Switch checked={!!s[key]} onCheckedChange={(v) => put({ [key]: v }, true)} aria-label={label} />;
  return (
    <div className="grid xl:grid-cols-2 gap-4 items-start">
      <section className="panel panel-pad">
        <h2 className="font-display text-[18px] font-semibold">Payout terms</h2>
        <p className="text-[12.5px] text-muted2 mt-1 mb-3">The defaults for every writer. Any writer can have their own terms — set those on the writer, from Payouts or Writers.</p>
        <Row title="Publisher percentage" hint="What the publishing company keeps from each writer's share before paying them.">
          <NumberBox value={draft.rate} unit="%" label="Publisher percentage" onChange={(v) => setDraft({ ...draft, rate: v })} />
        </Row>
        <Row title="Payout minimum" hint="A writer is ready to pay once their balance reaches this. Below it, the balance carries forward.">
          <NumberBox value={draft.min} unit="$" before label="Payout minimum" onChange={(v) => setDraft({ ...draft, min: v })} />
        </Row>
        <Row title="Payout schedule" hint="How often you plan to pay. Shown to writers as their next payout date.">
          <select aria-label="Payout schedule" value={s.schedule} onChange={(e) => put({ schedule: e.target.value })} className="h-10 rounded-lg border border-line bg-card px-3 text-[13.5px]">
            {["monthly", "quarterly", "twice a year", "when I choose"].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </Row>
        <Button
          className="bg-brand hover:bg-brand-hover mt-2"
          disabled={busy || (Number(draft.rate) === s.rate && Number(draft.min) === s.min)}
          onClick={() => put({ rate: Math.min(100, Math.max(0, Number(draft.rate) || 0)), min: Math.max(0, Number(draft.min) || 0) })}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save percentage and minimum"}
        </Button>
      </section>

      <div className="space-y-4">
        <section className="panel panel-pad">
          <h2 className="font-display text-[18px] font-semibold">What counts</h2>
          <p className="text-[12.5px] text-muted2 mt-1 mb-3">Which money goes into writers' balances.</p>
          <Row title="Only count money you've received" hint="Leaves out anything reported but not paid yet, such as a BMI accrual report.">
            {sw("paid_only", "Only count money you've received")}
          </Row>
          <Row title="Share BMI publisher income with writers" hint="BMI pays writers their own share directly. Switch this off if what BMI pays the publisher is yours to keep.">
            {sw("share_bmi", "Share BMI publisher income with writers")}
          </Row>
          <Row title="Carry negative balances forward" hint="If a service takes money back, the writer's balance goes below zero and is recovered from later income.">
            {sw("carry_negative", "Carry negative balances forward")}
          </Row>
          <Row title="Writer statements" hint="Keep a statement with every payout, showing the songs and the sum on that day.">
            {sw("statements", "Writer statements")}
          </Row>
        </section>
        <section className="panel panel-pad">
          <h2 className="font-display text-[18px] font-semibold">What writers can see</h2>
          <p className="text-[12.5px] text-muted2 mt-1 mb-3">
            When a writer signs in they see their balance, their songs with the final amount on each, and their payouts. Everything below is off unless you switch it on.
          </p>
          <Row title="Their split on each song" hint="The percentage of the song that's theirs.">
            {sw("show_split", "Show their split on each song")}
          </Row>
          <Row title="The publisher's percentage" hint="Their share before your cut, the percentage, and what's left.">
            {sw("show_fee", "Show the publisher's percentage")}
          </Row>
          <Row title="Whole-song income" hint="The full amount a song earned. This lets a writer work out what co-writers earned.">
            {sw("show_song", "Show whole-song income")}
          </Row>
          <Row title="Money not paid yet" hint="What's been reported but not paid to you, marked as pending.">
            {sw("show_pending", "Show money not paid yet")}
          </Row>
        </section>
      </div>
    </div>
  );
}
